/**
 * Unit toggle for the size chart modal.
 *
 * The table is authored in inches only; centimetres are derived here so there
 * is a single set of numbers to maintain. Each convertible cell carries its
 * inch value in `data-in`, which may be a single number ("44") or a range
 * ("32-34").
 *
 * Progressive enhancement: with this script blocked the table still renders
 * correctly in inches and the modal still opens, since the dialog itself is
 * driven by the theme's own dialog component.
 */

const INCHES_TO_CM = 2.54;

/** @param {string} value */
function toCentimetres(value) {
  return value
    .split(/\s*[-–]\s*/)
    .map((part) => {
      const inches = Number.parseFloat(part);
      if (Number.isNaN(inches)) return part;
      // One decimal is the useful precision for garment measurements; whole
      // numbers stay whole so the column doesn't fill with trailing ".0".
      const cm = inches * INCHES_TO_CM;
      return (Math.round(cm * 10) / 10).toString();
    })
    .join('–');
}

/** @param {string} value */
function toInches(value) {
  return value.replace(/-/g, '–');
}

/** @param {HTMLElement} root @param {'in' | 'cm'} unit */
function applyUnit(root, unit) {
  for (const cell of root.querySelectorAll('[data-in]')) {
    const source = cell.getAttribute('data-in');
    if (!source) continue;
    cell.textContent = unit === 'cm' ? toCentimetres(source) : toInches(source);
  }

  for (const label of root.querySelectorAll('.size-chart__unit-label')) {
    label.textContent = unit === 'cm' ? '(cm)' : '(in)';
  }

  for (const button of root.querySelectorAll('.size-chart__unit')) {
    const isActive = button.getAttribute('data-unit') === unit;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
  }
}

function init(root) {
  if (root.dataset.unitsBound === 'true') return;
  root.dataset.unitsBound = 'true';

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('.size-chart__unit') : null;
    if (!target) return;

    const unit = target.getAttribute('data-unit');
    if (unit === 'in' || unit === 'cm') applyUnit(root, unit);
  });
}

for (const root of document.querySelectorAll('.size-chart')) {
  if (root instanceof HTMLElement) init(root);
}
